import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../hooks/useSEO';
import { SITE_URL } from '../../utils/siteConfig';

const cookieTypes = [
  {
    icon: 'ri-shield-check-line',
    type: 'Noodzakelijke cookies',
    purpose: 'Essentieel voor het functioneren van de website',
    duration: 'Sessie / 1 jaar',
    examples: ['Sessie-ID', 'CSRF-token', 'Cookie-toestemming'],
    required: true,
  },
  {
    icon: 'ri-bar-chart-line',
    type: 'Analytische cookies',
    purpose: 'Meten van websitegebruik en verbetering van gebruikerservaring',
    duration: '2 jaar',
    examples: ['Google Analytics (_ga, _gid)', 'Paginabezoeken', 'Bouncepercentage'],
    required: false,
  },
  {
    icon: 'ri-advertisement-line',
    type: 'Marketing cookies',
    purpose: 'Gepersonaliseerde advertenties en retargeting',
    duration: '90 dagen – 1 jaar',
    examples: ['Google Ads', 'Facebook Pixel', 'LinkedIn Insight Tag'],
    required: false,
  },
];

export default function CookiebeleidPage() {
  useSEO({
    title: 'Cookiebeleid | Direct Uit China (DUC) — AVG/GDPR Compliant',
    description: 'Lees het cookiebeleid van Direct Uit China (DUC). Wij leggen uit welke cookies wij gebruiken, waarom en hoe u uw voorkeuren kunt beheren.',
    keywords: 'cookiebeleid DUC, cookies Direct Uit China, AVG GDPR cookies, privacybeleid cookies',
    canonical: `${SITE_URL}/cookiebeleid`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Cookiebeleid', item: `${SITE_URL}/cookiebeleid` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Cookiebeleid — Direct Uit China (DUC)',
        description: 'Cookiebeleid van DUC conform de AVG/GDPR. Wij leggen uit welke cookies wij gebruiken en hoe u uw voorkeuren kunt beheren.',
        url: `${SITE_URL}/cookiebeleid`,
        inLanguage: 'nl-NL',
        dateModified: '2026-03-26',
        publisher: { '@type': 'Organization', name: 'Direct Uit China (DUC)', url: SITE_URL },
      },
    ],
  });

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="bg-[#0A1628] pt-[100px] sm:pt-[116px] lg:pt-[136px] pb-16 overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-[#3B9EE8] text-sm mb-8 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line" /> Terug naar home
          </Link>
          <span className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6">
            <i className="ri-cookie-line" /> Cookiebeleid
          </span>
          <h1 className="font-heading font-800 text-white text-4xl md:text-5xl leading-tight mb-5">
            Cookiebeleid
          </h1>
          <p className="text-white/60 text-base leading-relaxed max-w-2xl">
            Direct Uit China (DUC) is transparant over het gebruik van cookies. Op deze pagina leggen wij uit welke cookies wij gebruiken, waarvoor en hoe u uw voorkeuren kunt beheren.
          </p>
          <div className="flex flex-wrap gap-4 mt-6 pt-6 border-t border-white/10">
            <div className="flex items-center gap-2 text-white/45 text-xs">
              <i className="ri-calendar-line text-[#3B9EE8]" />
              Laatste update: maart 2025
            </div>
            <div className="flex items-center gap-2 text-white/45 text-xs">
              <i className="ri-shield-check-line text-[#3B9EE8]" />
              AVG/GDPR-compliant
            </div>
            <div className="flex items-center gap-2 text-white/45 text-xs">
              <i className="ri-flag-line text-[#3B9EE8]" />
              Van toepassing op directuitchina.nl
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">

          {/* Wat zijn cookies */}
          <div className="mb-12 p-8 bg-[#F7F8FA] rounded-2xl border border-[#0A1628]/6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#3B9EE8]/12">
                <i className="ri-question-line text-[#3B9EE8] text-lg" />
              </div>
              <h2 className="font-heading font-700 text-[#0A1628] text-xl">Wat zijn cookies?</h2>
            </div>
            <p className="text-[#0A1628]/65 text-base leading-relaxed mb-4">
              Cookies zijn kleine tekstbestanden die op uw apparaat worden opgeslagen wanneer u onze website bezoekt. Ze helpen ons de website goed te laten functioneren, het gebruik te analyseren en u relevante content te tonen.
            </p>
            <p className="text-[#0A1628]/65 text-base leading-relaxed">
              Wij respecteren uw privacy en verwerken gegevens conform de Algemene Verordening Gegevensbescherming (AVG), ook bekend als de GDPR.
            </p>
          </div>

          {/* Cookie overzicht */}
          <h2 className="font-heading font-800 text-[#0A1628] text-2xl mb-6 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#3B9EE8]/12">
              <i className="ri-list-check text-[#3B9EE8] text-sm" />
            </div>
            Welke cookies gebruiken wij?
          </h2>

          <div className="space-y-5 mb-12">
            {cookieTypes.map((c) => (
              <div key={c.type} className="rounded-xl border border-[#0A1628]/8 overflow-hidden">
                <div className={`flex items-center gap-4 p-5 ${c.required ? 'bg-[#3B9EE8]/5 border-b border-[#3B9EE8]/15' : 'bg-[#F7F8FA] border-b border-[#0A1628]/6'}`}>
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${c.required ? 'bg-[#3B9EE8]/15' : 'bg-[#0A1628]/8'}`}>
                    <i className={`${c.icon} ${c.required ? 'text-[#3B9EE8]' : 'text-[#0A1628]/60'} text-base`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-700 text-[#0A1628] text-base">{c.type}</span>
                      {c.required && (
                        <span className="text-[#3B9EE8] text-xs font-500 bg-[#3B9EE8]/10 px-2 py-0.5 rounded-full">Verplicht</span>
                      )}
                    </div>
                    <p className="text-[#0A1628]/55 text-sm mt-0.5">{c.purpose}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-[#0A1628]/40 text-xs">Bewaartijd</div>
                    <div className="text-[#0A1628]/70 text-sm font-500 mt-0.5">{c.duration}</div>
                  </div>
                </div>
                <div className="p-5">
                  <div className="text-[#0A1628]/50 text-xs mb-2 font-600 uppercase tracking-wide">Voorbeelden</div>
                  <div className="flex flex-wrap gap-2">
                    {c.examples.map((ex) => (
                      <span key={ex} className="bg-[#F7F8FA] border border-[#0A1628]/8 rounded-full px-3 py-1 text-xs text-[#0A1628]/65 font-500">{ex}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Rechten */}
          <div className="mb-12">
            <h2 className="font-heading font-800 text-[#0A1628] text-2xl mb-6 flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#3B9EE8]/12">
                <i className="ri-user-settings-line text-[#3B9EE8] text-sm" />
              </div>
              Uw rechten & voorkeuren
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: 'ri-settings-3-line', title: 'Toestemming beheren', desc: 'U kunt uw cookievoorkeuren op elk moment aanpassen via de cookiebanner die onderaan de pagina verschijnt.' },
                { icon: 'ri-delete-bin-line', title: 'Cookies verwijderen', desc: 'Via de instellingen van uw browser kunt u cookies verwijderen of het plaatsen ervan blokkeren.' },
                { icon: 'ri-eye-off-line', title: 'Tracking opt-out', desc: 'U kunt zich afmelden voor Google Analytics via tools.google.com/dlpage/gaoptout.' },
                { icon: 'ri-mail-send-line', title: 'Contact opnemen', desc: 'Vragen over ons cookiebeleid? Stuur een e-mail naar Info@directuitchina.nl of bel +31 6 24 35 91 62.' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4 p-5 bg-[#F7F8FA] rounded-xl border border-[#0A1628]/6">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#3B9EE8]/12 flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-[#3B9EE8] text-base`} />
                  </div>
                  <div>
                    <div className="font-700 text-[#0A1628] text-sm mb-1">{item.title}</div>
                    <p className="text-[#0A1628]/55 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Grondslagen */}
          <div className="mb-12 p-8 bg-[#0A1628] rounded-2xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#3B9EE8]/20">
                <i className="ri-scales-line text-[#3B9EE8] text-lg" />
              </div>
              <h2 className="font-heading font-700 text-white text-xl">Juridische grondslagen (AVG art. 6)</h2>
            </div>
            <div className="space-y-3">
              {[
                { grond: 'Toestemming (art. 6 lid 1a)', detail: 'Voor analytische en marketingcookies vragen wij expliciete toestemming via de cookiebanner.' },
                { grond: 'Gerechtvaardigd belang (art. 6 lid 1f)', detail: 'Voor noodzakelijke cookies die de werking van de website garanderen.' },
                { grond: 'Recht op intrekking', detail: 'U kunt uw toestemming te allen tijde intrekken, zonder dat dit invloed heeft op reeds verwerkte gegevens.' },
              ].map((item) => (
                <div key={item.grond} className="flex items-start gap-3 p-4 bg-white/5 border border-white/8 rounded-xl">
                  <i className="ri-check-double-line text-[#3B9EE8] text-base mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-600 text-white text-sm mb-0.5">{item.grond}</div>
                    <div className="text-white/55 text-xs leading-relaxed">{item.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="p-8 bg-[#F7F8FA] rounded-2xl border border-[#0A1628]/6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#3B9EE8]/12">
                <i className="ri-building-line text-[#3B9EE8] text-lg" />
              </div>
              <h2 className="font-heading font-700 text-[#0A1628] text-xl">Verwerkingsverantwoordelijke</h2>
            </div>
            <div className="space-y-2 text-[#0A1628]/65 text-sm">
              <div className="flex items-center gap-2"><i className="ri-store-2-line text-[#3B9EE8]" /> <strong className="text-[#0A1628]">Direct Uit China (DUC)</strong></div>
              <div className="flex items-center gap-2"><i className="ri-mail-line text-[#3B9EE8]" /> <a href="mailto:Info@directuitchina.nl" className="hover:text-[#3B9EE8] transition-colors">Info@directuitchina.nl</a></div>
              <div className="flex items-center gap-2"><i className="ri-phone-line text-[#3B9EE8]" /> <a href="tel:+31624359162" className="hover:text-[#3B9EE8] transition-colors">+31 6 24 35 91 62</a></div>
              <div className="flex items-center gap-2"><i className="ri-global-line text-[#3B9EE8]" /> <a href="https://directuitchina.nl" className="hover:text-[#3B9EE8] transition-colors">directuitchina.nl</a></div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-4 justify-between items-center pt-8 border-t border-[#0A1628]/6">
            <div className="flex flex-wrap gap-4">
              <Link to="/privacybeleid" className="inline-flex items-center gap-2 text-[#3B9EE8] hover:text-[#2D85C5] text-sm font-600 transition-colors whitespace-nowrap">
                <i className="ri-shield-line" /> Privacybeleid
              </Link>
              <Link to="/algemene-voorwaarden" className="inline-flex items-center gap-2 text-[#3B9EE8] hover:text-[#2D85C5] text-sm font-600 transition-colors whitespace-nowrap">
                <i className="ri-file-text-line" /> Algemene voorwaarden
              </Link>
            </div>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white text-sm font-700 px-5 py-2.5 rounded-lg transition-all whitespace-nowrap cursor-pointer">
              Contact opnemen <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
